import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState, AppThunk } from "../../app/store";
import { MAX_HP } from "../../constants/GameConstants";

interface Unit {
  x: number;
  y: number;
  hp: number;
  alive?: boolean;
  // Waypoints still to be walked, [x, y] each, oldest first. Only present for
  // units following a queued path.
  route?: number[][];
  status: "idle" | "loading" | "failed";
  last: {
    x: number;
    y: number;
    status: "idle" | "loading" | "failed";
  };
}

interface Player {
  units: Record<string, Unit>;
}

type Players = Record<string, Player>;

var initialState: Players = {
  "0": {
    units: {
      "0": {
        x: 100,
        y: 100,
        hp: 100,
        status: "idle",
        last: {
          x: 100,
          y: 100,
          status: "idle",
        },
      },
      "1": {
        x: 200,
        y: 200,
        hp: 100,
        status: "idle",
        last: {
          x: 100,
          y: 100,
          status: "idle",
        },
      },
    },
  },
  "1": {
    units: {
      "0": {
        x: 400,
        y: 400,
        hp: 100,
        status: "idle",
        last: {
          x: 100,
          y: 100,
          status: "idle",
        },
      },
      "1": {
        x: 300,
        y: 300,
        hp: 100,
        status: "idle",
        last: {
          x: 100,
          y: 100,
          status: "idle",
        },
      },
    },
  },
};

export const gameSlice = createSlice({
  name: "game",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    /*updatePlayer: (state, action) => {
      // Redux Toolkit allows us to write "mutating" logic in reducers. It
      // doesn't actually mutate the state because it uses the Immer library,
      // which detects changes to a "draft state" and produces a brand new
      // immutable state based off those changes
      state[action.payload.id].x = action.payload.x;
      state[action.payload.id].y = action.payload.y;
    }, */
    updatePlayers: (state, action) => {
      const playersData: {
        playerId: {
          units: {
            unitId: {
              x: string;
              y: string;
              hp: string;
              alive: boolean;
              route: number[][];
            };
          };
        };
      } = action.payload;
      for (const [playerId, unitsDict] of Object.entries(playersData)) {
        // The server decides how many sides and units a match has, so take
        // whatever the snapshot carries instead of assuming initialState's
        // shape. Anything it has never seen before is created on the spot.
        if (state[playerId] === undefined) {
          state[playerId] = { units: {} };
        }
        const player = state[playerId];
        for (const [unitId, position] of Object.entries(unitsDict.units)) {
          if (player.units[unitId] === undefined) {
            // Seed a new unit where it already is, so the `last` position below
            // doesn't have the renderer sliding it in from somewhere it never
            // stood.
            player.units[unitId] = {
              x: Number(position.x),
              y: Number(position.y),
              hp: MAX_HP,
              status: "idle",
              last: {
                x: Number(position.x),
                y: Number(position.y),
                status: "idle",
              },
            };
          }
          const unit = player.units[unitId];
          unit.last = { x: unit.x, y: unit.y, status: unit.status };
          unit.x = Number(position.x);
          unit.y = Number(position.y);
          // hp only arrives when it changed, so keep the old value otherwise
          if (position.hp !== undefined) {
            unit.hp = Number(position.hp);
          }
          if (position.alive !== undefined) {
            unit.alive = Boolean(position.alive);
          }
          // Like hp, the route is only sent while it exists plus once when it
          // empties — an absent route means unchanged, not gone.
          if (position.route !== undefined) {
            unit.route = position.route;
          }
        }
      }
    },
  },
});

export const { updatePlayers } = gameSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectPlayers = (state: RootState) => state.players;

/* // We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
export const incrementIfOdd =
  (amount: number): AppThunk =>
  (dispatch, getState) => {
    const currentValue = selectPlayer(getState());
    if (currentValue % 2 === 1) {
      dispatch(incrementByAmount(amount));
    }
  }; */

export default gameSlice.reducer;
